import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { formatCurrency, formatDate } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, AlertTriangle, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Orders() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [orderstatus, setOrderstatus] = useState("alle");
  const [verzendkanaal, setVerzendkanaal] = useState("alle");
  const [alleenBetaald, setAlleenBetaald] = useState(false);
  const [alleenControle, setAlleenControle] = useState(false);
  const [sort, setSort] = useState("datum_desc");
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [statusOpties, setStatusOpties] = useState([]);

  useEffect(() => {
    api.get("/settings/orderstatus").then(({ data }) => setStatusOpties(data || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {
      page, page_size: pageSize, search,
      sort, alleen_betaald: alleenBetaald, alleen_controle: alleenControle,
    };
    if (orderstatus && orderstatus !== "alle") params.orderstatus = orderstatus;
    if (verzendkanaal && verzendkanaal !== "alle") params.verzendkanaal = verzendkanaal;
    api.get("/orders", { params })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [page, pageSize, search, orderstatus, verzendkanaal, alleenBetaald, alleenControle, sort]);

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  const openDetail = async (ordernummer) => {
    const { data } = await api.get(`/orders/${ordernummer}`);
    setDetail(data);
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Bestellingen</p>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mt-1">Orders</h1>
        </div>
        {isOwner && (
          <Button
            variant="outline"
            className="border-slate-300"
            onClick={() => {
            const params = new URLSearchParams();
            if (search) params.set("search", search);
            if (orderstatus && orderstatus !== "alle") params.set("orderstatus", orderstatus);
            if (verzendkanaal && verzendkanaal !== "alle") params.set("verzendkanaal", verzendkanaal);
            if (alleenBetaald) params.set("alleen_betaald", "true");
            if (alleenControle) params.set("alleen_controle", "true");
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/orders-export?${params.toString()}`;
            // Use fetch + blob so the auth cookie is sent (anchor download doesn't always include cookies)
            fetch(url, { credentials: "include" })
              .then(async (r) => {
                if (!r.ok) {
                  let msg = `Export mislukt (HTTP ${r.status})`;
                  try {
                    const err = await r.json();
                    if (err?.detail) msg = err.detail;
                  } catch (_) { /* not JSON — keep default */ }
                  toast.error(msg);
                  return null;
                }
                return r.blob();
              })
              .then((blob) => {
                if (!blob) return;
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `orders-${verzendkanaal === "alle" ? "alle" : verzendkanaal.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
                URL.revokeObjectURL(a.href);
              })
              .catch((e) => toast.error(`Export mislukt: ${e?.message || e}`));
          }}
          data-testid="orders-export-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
        )}
      </header>

      <Card className="bg-white border-slate-200 p-5 rounded-lg">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-slate-500 mb-1.5 block">Zoeken</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Ordernr, e-mail, woonplaats, bedrijfsnaam…"
                className="pl-9"
                data-testid="orders-search-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1.5 block">Status</Label>
            <Select value={orderstatus} onValueChange={(v) => { setOrderstatus(v); setPage(1); }}>
              <SelectTrigger className="w-44" data-testid="orders-status-select">
                <SelectValue placeholder="Alle statussen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle statussen</SelectItem>
                {statusOpties.map((s) => (
                  <SelectItem key={s.status_naam} value={s.status_naam}>{s.status_naam}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1.5 block">Verzending</Label>
            <Select value={verzendkanaal} onValueChange={(v) => { setVerzendkanaal(v); setPage(1); }}>
              <SelectTrigger className="w-40" data-testid="orders-shipping-select">
                <SelectValue placeholder="Alle kanalen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle kanalen</SelectItem>
                <SelectItem value="Webship">📦 Webship</SelectItem>
                <SelectItem value="Gheeraert">🏗 Gheeraert</SelectItem>
                <SelectItem value="Mix">Mix</SelectItem>
                <SelectItem value="Onbekend">Onbekend</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1.5 block">Sortering</Label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-48" data-testid="orders-sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="datum_desc">Datum (nieuwste eerst)</SelectItem>
                <SelectItem value="datum_asc">Datum (oudste eerst)</SelectItem>
                <SelectItem value="omzet_desc">Omzet (hoog → laag)</SelectItem>
                <SelectItem value="omzet_asc">Omzet (laag → hoog)</SelectItem>
                <SelectItem value="marge_desc">Marge (hoog → laag)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-4 pb-1.5">
            <div className="flex items-center gap-2">
              <Switch
                checked={alleenBetaald}
                onCheckedChange={(v) => { setAlleenBetaald(v); setPage(1); }}
                data-testid="orders-paid-only-switch"
              />
              <Label className="text-sm">Alleen betaald</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={alleenControle}
                onCheckedChange={(v) => { setAlleenControle(v); setPage(1); }}
                data-testid="orders-control-only-switch"
              />
              <Label className="text-sm">Alleen controle</Label>
            </div>
          </div>
        </div>
      </Card>

      <Card className="bg-white border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead>
              <tr className="bg-slate-50 border-y border-slate-200 text-slate-600">
                <th className="text-left py-3 px-4 font-semibold">Ordernr</th>
                <th className="text-left py-3 px-4 font-semibold">Datum</th>
                <th className="text-left py-3 px-4 font-semibold">Status</th>
                <th className="text-left py-3 px-4 font-semibold">Klant & producten</th>
                <th className="text-left py-3 px-4 font-semibold">Verzending</th>
                <th className="text-left py-3 px-4 font-semibold">Regio</th>
                <th className="text-right py-3 px-4 font-semibold">Omzet incl.</th>
                <th className="text-right py-3 px-4 font-semibold">Omzet excl.</th>
                <th className="text-right py-3 px-4 font-semibold">Marge</th>
                <th className="text-center py-3 px-4 font-semibold">Flags</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="10" className="text-center text-slate-400 py-8">Laden…</td></tr>}
              {!loading && data.items?.length === 0 && (
                <tr><td colSpan="10" className="text-center text-slate-400 py-8">Geen orders gevonden.</td></tr>
              )}
              {data.items?.map((o) => (
                <tr
                  key={o.ordernummer}
                  className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer transition-colors"
                  onClick={() => openDetail(o.ordernummer)}
                  data-testid={`order-row-${o.ordernummer}`}
                >
                  <td className="py-2.5 px-4 font-medium text-slate-900">{o.ordernummer}</td>
                  <td className="py-2.5 px-4 text-slate-600">{formatDate(o.datum)}</td>
                  <td className="py-2.5 px-4">
                    <Badge variant="outline" className={
                      o.is_betaald ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-100 text-slate-600 border-slate-200"
                    }>
                      {o.orderstatus}
                    </Badge>
                  </td>
                  <td className="py-2.5 px-4 text-slate-700 max-w-[280px]">
                    <div className="font-medium">{o.voornaam} {o.achternaam}</div>
                    {o.bedrijfsnaam && <div className="text-xs text-slate-500">{o.bedrijfsnaam}</div>}
                    {o.producten_omschrijving && (
                      <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2" title={o.producten_omschrijving}>
                        {o.producten_omschrijving}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 px-4">
                    <ShippingBadge channel={o.verzendkanaal} />
                  </td>
                  <td className="py-2.5 px-4 text-slate-600">
                    {o.postcode} {o.woonplaats} <span className="text-slate-400">· {o.land}</span>
                  </td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-slate-900">{formatCurrency(o.prijs_incl_btw)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-slate-600">{formatCurrency(o.prijs_excl_btw)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums">
                    {o.operationele_marge != null ? (
                      <span className={o.operationele_marge >= 0 ? "text-emerald-700 font-medium" : "text-rose-700 font-medium"}>
                        {formatCurrency(o.operationele_marge)}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="py-2.5 px-4 text-center">
                    <div className="flex justify-center gap-1">
                      {o.btw_verlegd && <Badge className="bg-sky-50 text-sky-700 border-sky-200 text-[10px]">BTW verlegd</Badge>}
                      {o.controle_nodig && <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]"><AlertTriangle className="h-3 w-3 mr-0.5" />Controle</Badge>}
                      {o.is_gewijzigd && <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Gewijzigd</Badge>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
          <p className="text-xs text-slate-500">
            Pagina {page} van {totalPages} · {data.total} orders
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="orders-prev-btn">Vorige</Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="orders-next-btn">Volgende</Button>
          </div>
        </div>
      </Card>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Order {detail?.order?.ordernummer}</DialogTitle>
          </DialogHeader>
          {detail?.order && (
            <div className="space-y-5 text-sm">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Field label="Datum" value={formatDate(detail.order.datum)} />
                <Field label="Status" value={detail.order.orderstatus} />
                <Field label="Betaalmethode" value={detail.order.betaalmethode} />
                <Field label="Klanttype" value={detail.order.klanttype} />
                <Field label="Omzet incl. btw" value={formatCurrency(detail.order.prijs_incl_btw)} />
                <Field label="Omzet excl. btw" value={formatCurrency(detail.order.prijs_excl_btw)} />
                <Field label="BTW" value={formatCurrency(detail.order.btw_bedrag) + ` (${detail.order.btw_tarief}%)` + (detail.order.btw_verlegd ? " · verlegd" : "")} />
                <Field label="Verzendkosten" value={formatCurrency(detail.order.verzendkosten)} />
                <Field label="Brutomarge" value={formatCurrency(detail.order.brutomarge)} />
                <Field label="Betaalkost" value={formatCurrency(detail.order.betaalkost)} />
                <Field label="Boekhoudkost" value={formatCurrency(detail.order.boekhoudkost)} />
                <Field label="Operationele marge" value={formatCurrency(detail.order.operationele_marge)} />
              </div>
              {detail.order.controle_redenen?.length > 0 && (
                <div className="p-3 rounded-md bg-amber-50 border border-amber-200">
                  <p className="text-xs font-semibold text-amber-800 mb-1">Controlepunten</p>
                  <ul className="list-disc list-inside text-xs text-amber-900">
                    {detail.order.controle_redenen.map((r, i) => <li key={`${i}-${r.slice(0,20)}`}>{r}</li>)}
                  </ul>
                </div>
              )}
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-2">Orderregels</p>
                <table className="w-full text-xs border-collapse" data-testid="order-detail-regels">
                  <thead>
                    <tr className="bg-slate-50 border-y border-slate-200 text-slate-600">
                      <th className="text-left py-2 px-2">Product</th>
                      <th className="text-right py-2 px-2">Aantal</th>
                      <th className="text-right py-2 px-2">Verkoop excl.</th>
                      <th className="text-right py-2 px-2">Inkoop</th>
                      <th className="text-right py-2 px-2">Marge</th>
                      <th className="text-right py-2 px-2">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.regels.map((r) => (
                      <tr key={r.id} className="border-b border-slate-100">
                        <td className="py-2 px-2">
                          <span className="text-slate-700">{r.productnaam}</span>
                          <span className="block text-[10px] text-slate-400">{r.productnummer}</span>
                        </td>
                        <td className="py-2 px-2 text-right">{r.aantal}</td>
                        <td className="py-2 px-2 text-right">{formatCurrency(r.totaal_verkoop_excl_btw)}</td>
                        <td className="py-2 px-2 text-right">{r.totaal_inkoop != null ? formatCurrency(r.totaal_inkoop) : <span className="text-amber-600">Ontbreekt</span>}</td>
                        <td className="py-2 px-2 text-right">{r.brutomarge != null ? formatCurrency(r.brutomarge) : "—"}</td>
                        <td className="py-2 px-2 text-right">{r.brutomarge_percentage != null ? `${r.brutomarge_percentage.toFixed(1)}%` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-slate-500">{label}</p>
      <p className="text-sm text-slate-900 font-medium mt-0.5">{value}</p>
    </div>
  );
}

function ShippingBadge({ channel }) {
  if (!channel || channel === "Onbekend") {
    return <span className="text-xs text-slate-400">—</span>;
  }
  if (channel === "Webship") {
    return (
      <Badge
        variant="outline"
        className="bg-violet-50 text-violet-700 border-violet-200 font-medium"
        data-testid="shipping-webship"
        title="Verzending via Webship (dozen)"
      >
        📦 Webship
      </Badge>
    );
  }
  if (channel === "Gheeraert") {
    return (
      <Badge
        variant="outline"
        className="bg-orange-50 text-orange-700 border-orange-200 font-medium"
        data-testid="shipping-gheeraert"
        title="Verzending via Gheeraert (paletten)"
      >
        🏗 Gheeraert
      </Badge>
    );
  }
  if (channel === "Mix") {
    return (
      <Badge
        variant="outline"
        className="bg-slate-100 text-slate-700 border-slate-300 font-medium"
        data-testid="shipping-mix"
        title="Gemengd: dozen + paletten in dezelfde order"
      >
        Mix
      </Badge>
    );
  }
  return <Badge variant="outline">{channel}</Badge>;
}
